import React, {useEffect} from 'react';
import {Box, Input} from "@chakra-ui/react";

interface SelectWithAutoCompleteProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  items: string[];
  selectedItem: string;
  onSelectItem: (item: string) => void;
  onChange: (item: string) => void;
  placeholder: string;
  label?: string;
}

const SelectWithAutoComplete: React.FC<SelectWithAutoCompleteProps> = (
  {
    isOpen,
    setIsOpen,
    items,
    selectedItem,
    onSelectItem,
    onChange,
    placeholder,
    label
  }: SelectWithAutoCompleteProps) => {
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);

  const filteredItems: string[] = items.filter((item: string) =>
    item.toLowerCase().includes(selectedItem.toLowerCase())
  );

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({block: "nearest"});
    }
  }, [highlightedIndex]);


  return (
    <Box position="relative" w="100%">
      {label && (
        <div className="flex mb-2 base2 font-semibold">
          {label}
        </div>)
      }
      <Input
        h={"50px"}
        p={2}
        autoFocus={false}
        placeholder={placeholder}
        value={selectedItem}
        onChange={(e) => {
          if (!isOpen) {
            setIsOpen(true)
          }
          onChange(e.target.value);
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        onClick={() => setIsOpen(true)}
        onBlur={() => {
          // Delay to allow click on dropdown items
          setTimeout(() => setIsOpen(false), 100);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
              Math.min(prev + 1, filteredItems.length - 1)
            );
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          } else if (e.key === "Enter" && highlightedIndex >= 0) {
            e.preventDefault();
            onSelectItem(filteredItems[highlightedIndex]);
            setIsOpen(false);
          }
        }}
        color="white"
        border="1px solid gray"
        _focus={{borderColor: "brandPurple.500"}}
        sx={{
          border: "none",
          outline: "none",
          boxShadow: "0 0 0 0.5px gray",
          color: "white",
          _focus: {
            boxShadow: "0 0 0 1px brandPurple.500",
          },
          _hover: {
            boxShadow: "0 0 0 1px brandPurple.500",
          },
        }}
      />
      {isOpen && filteredItems.length > 0 && (
        <Box
          position="absolute"
          zIndex="10"
          bg="black"
          mt={1}
          w="100%"
          maxH="200px"
          overflowY="auto"
          border="1px solid gray"
          borderRadius="md"
        >
          {filteredItems.map((item: string, index: number) => (
            <Box
              key={item}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              px={4}
              py={2}
              _hover={{bg: "brandPurple.500", cursor: "pointer"}}
              bg={highlightedIndex === index ? "brandPurple.500" : ""}
              color="white"
              onMouseEnter={() => setHighlightedIndex(index)}
              onMouseDown={() => {
                onSelectItem(item);
                setIsOpen(false);
              }}
            >
              {item}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default SelectWithAutoComplete;